import { Link } from '@remix-run/react';
import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { CollectionsQuery } from '~/generated/graphql';
import { getIconForCollection } from '~/utils/category-icons';

interface SidebarProps {
    collections: CollectionsQuery['collections']['items'];
    activeSlug?: string;
}


export function Sidebar({ collections, activeSlug }: SidebarProps) {
    // Find which parent should be auto-expanded based on activeSlug
    const findParentWithActiveChild = () => {
        if (!activeSlug) return new Set<string>();
        const parentIds = new Set<string>();

        for (const collection of collections) {
            // Check if this collection is active
            if (collection.slug === activeSlug) {
                parentIds.add(collection.id);
            }
            // Check if any child is active
            if (collection.children?.some(child => child.slug === activeSlug)) {
                parentIds.add(collection.id);
            }
        }
        return parentIds;
    };

    const [expandedIds, setExpandedIds] = useState<Set<string>>(() => findParentWithActiveChild());

    if (!collections || collections.length === 0) {
        return null;
    }

    // Filter to only show top-level collections (no parent or parent is root)
    const topLevelCollections = collections.filter(
        (c) => !c.parent || c.parent.name === '__root_collection__'
    );

    const toggleExpand = (id: string) => {
        setExpandedIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    // Check if a slug is active (either exact match or parent of active)
    const isActive = (slug: string) => slug === activeSlug;

    return (
        <aside className="hidden lg:block w-[180px] xl:w-[200px] flex-shrink-0 self-start sticky top-20">
            {/* Navigation List */}
            <nav className="py-1">
                {topLevelCollections.slice(0, 14).map((collection) => {
                    const iconClass = getIconForCollection(collection.name);
                    const hasChildren = collection.children && collection.children.length > 0;
                    const isExpanded = expandedIds.has(collection.id);

                    return (
                        <div key={collection.id}>
                            {/* Parent Category */}
                            <div
                                className={`group flex items-center gap-2.5 px-2 py-2 cursor-pointer font-medium text-sm leading-5 tracking-tight rounded-lg transition-colors ${isActive(collection.slug)
                                    ? 'bg-[#F7E4E9] text-[#1A090E]'
                                    : 'text-[#54454A] hover:bg-gray-50'
                                    }`}
                                style={{ fontFamily: 'Poppins, sans-serif' }}
                                onClick={() => hasChildren && toggleExpand(collection.id)}
                            >
                                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                                    {collection.featuredAsset?.preview ? (
                                        <img
                                            src={`${collection.featuredAsset.preview}?w=32&h=32&format=webp`}
                                            alt={collection.name}
                                            className="w-5 h-5 object-contain"
                                        />
                                    ) : (
                                        <i className={`${iconClass} text-xl text-gray-400 group-hover:text-primary transition-colors`} />
                                    )}
                                </div>
                                <Link
                                    to={`/collections/${collection.slug}`}
                                    className="flex-1 truncate hover:text-primary transition-colors duration-200 font-medium"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {collection.name}
                                </Link>
                                {hasChildren ? (
                                    isExpanded ? (
                                        <ChevronDown size={14} className="text-primary flex-shrink-0" />
                                    ) : (
                                        <ChevronRight size={14} className="text-gray-300 group-hover:text-primary/50 flex-shrink-0" />
                                    )
                                ) : (
                                    <ChevronRight size={14} className="text-gray-300 group-hover:text-primary/50 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                                )}
                            </div>

                            {/* Sub-categories */}
                            {hasChildren && isExpanded && (
                                <div className="ml-6 mt-1 space-y-0.5">
                                    {collection.children!.map((child) => (
                                        <Link
                                            key={child.id}
                                            to={`/collections/${child.slug}`}
                                            className={`block py-2 px-3 transition-colors duration-200 font-medium text-sm leading-5 tracking-tight rounded-lg ${isActive(child.slug)
                                                ? 'bg-[#F7E4E9] text-[#1A090E]'
                                                : 'text-[#54454A] hover:text-primary hover:bg-gray-100'
                                                }`}
                                            style={{ fontFamily: 'Poppins, sans-serif' }}
                                        >
                                            {child.name}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </nav>
        </aside>
    );
}
